import {
  PlainExtension,
  FragmentStringHandlerOptions,
  NodeStringHandlerOptions,
  ProsemirrorNode,
  Static,
  StringHandlerOptions,
  getDocument,
  isDocNode,
} from "remirror";

import {
  DOMParser as PMDomParser,
  DOMSerializer,
  Fragment,
} from "@remirror/pm/model";

const defaultParseOptions = { preserveWhitespace: true } as const;

const startExprRe = /^\{\{(?!\s*\/)([^}]+)\}\}/i;
const endExprRe = /^\{\{\s*\/([^}]+)\}\}/i;
const sectionRe = /^\s*([\w-]+)-(start|end)\s*$/i;

const exprDelimiter = "_EXPR_";

const DATA_EXPR_START = "data-expr-start";
const DATA_EXPR_END = "data-expr-end";
const DATA_SECTION = "data-section";

function elementFromString(html: string, document?: Document): HTMLElement {
  const parser = new (
    (document || getDocument())?.defaultView ?? window
  ).DOMParser();
  return parser.parseFromString(`<body>${html}</body>`, "text/html").body;
}

const replaceComments = (el) => {
  let i = 0;
  while (i < el.childNodes.length) {
    const node = el.childNodes[i];
    if (node.nodeType === 8) {
      const commentText = node.textContent?.trim();

      if (commentText) {
        if (startExprRe.test(commentText)) {
          const existingStartExpr =
            node.nextElementSibling?.getAttribute(DATA_EXPR_START);
          const newStartExpr = existingStartExpr
            ? `${existingStartExpr} ${exprDelimiter} ${commentText}`
            : commentText;
          node.nextElementSibling?.setAttribute(DATA_EXPR_START, newStartExpr);
        } else if (endExprRe.test(commentText)) {
          const existingEndExpr =
            node.previousElementSibling?.getAttribute(DATA_EXPR_END);
          const newEndExpr = existingEndExpr
            ? `${existingEndExpr} ${exprDelimiter} ${commentText}`
            : commentText;
          node.previousElementSibling?.setAttribute(DATA_EXPR_END, newEndExpr);
        } else if (sectionRe.test(commentText)) {
          const divNode = el.ownerDocument.createElement("div");
          divNode.setAttribute(DATA_SECTION, commentText);
          divNode.setAttribute("style", "display:none;margin:0;padding:0;");
          el.replaceChild(divNode, node);
          i++;
          continue;
        }
      }

      if (node.parentNode === el) {
        el.removeChild(node);
      } else {
        i++;
      }
    } else {
      if (node.nodeType === 1 || node.nodeType === 11) {
        replaceComments(node);
      }
      i++;
    }
  }
};

const replaceExpressions = (el) => {
  for (let i = 0; i < el.childNodes.length; i++) {
    const node = el.childNodes[i];
    if (node.nodeType === 1) {
      if (node.hasAttribute(DATA_EXPR_START)) {
        const startExprs = node
          .getAttribute(DATA_EXPR_START)
          .split(exprDelimiter);
        startExprs.forEach((expr) => {
          const comment = document.createComment(expr.trim());
          el.insertBefore(comment, node);
        });
        node.removeAttribute(DATA_EXPR_START);
      }
      if (node.hasAttribute(DATA_EXPR_END)) {
        const endExprs = node.getAttribute(DATA_EXPR_END).split(exprDelimiter);
        endExprs.reverse().forEach((expr) => {
          const comment = document.createComment(expr.trim());
          node.after(comment);
        });
        node.removeAttribute(DATA_EXPR_END);
      }

      if (node.hasAttribute(DATA_SECTION)) {
        const sectionValue = node.getAttribute(DATA_SECTION);
        const comment = document.createComment(sectionValue);
        el.replaceChild(comment, node);
      }

      replaceExpressions(node);
    }
  }
};

const removeDataAttributes = (element) => {
  if (element.attributes) {
    const attributesToRemove = [];
    for (const attr of element.attributes) {
      const isPageLink =
        element.tagName === "SPAN" &&
        element.classList.contains("cm-page-link");
      const isSpecialDataAttr =
        attr.name === DATA_EXPR_START ||
        attr.name === DATA_EXPR_END ||
        attr.name === DATA_SECTION;

      if (attr.name.startsWith("data-") && !isPageLink && !isSpecialDataAttr) {
        attributesToRemove.push(attr.name);
      }
    }
    for (const attrName of attributesToRemove) {
      element.removeAttribute(attrName);
    }
  }

  for (const child of element.children) {
    removeDataAttributes(child);
  }
};

export function cmCodeToProsemirrorNode(
  props: FragmentStringHandlerOptions
): Fragment;
export function cmCodeToProsemirrorNode(
  props: NodeStringHandlerOptions
): ProsemirrorNode;
export function cmCodeToProsemirrorNode(
  props: StringHandlerOptions
): ProsemirrorNode | Fragment {
  const {
    content,
    schema,
    document,
    fragment = false,
    ...parseOptions
  } = props;
  // Replace section comments with placeholders before parsing
  //const htmlWithPlaceholders = preserveSectionComments(content);
  const element = elementFromString(content, document);

  replaceComments(element);

  const parser = PMDomParser.fromSchema(schema);

  const dom = fragment
    ? parser.parseSlice(element, { ...defaultParseOptions, ...parseOptions })
        .content
    : parser.parse(element, { ...defaultParseOptions, ...parseOptions });

  return dom;
}

export function prosemirrorCmCodeToHtml(
  node: ProsemirrorNode,
  document = getDocument()
): string {
  const element = document.createElement("div");
  element.append(prosemirrorCmCodeToDom(node, document));

  // Restore section comments from placeholders after serialization
  //restoreSectionComments(element);

  return element.innerHTML;
}

export function prosemirrorCmCodeToDom(
  node: ProsemirrorNode,
  document = getDocument()
): DocumentFragment | HTMLElement {
  const fragment = isDocNode(node, node.type.schema)
    ? node.content
    : Fragment.from(node);
  const dom = DOMSerializer.fromSchema(node.type.schema).serializeFragment(
    fragment,
    { document }
  );

  replaceExpressions(dom as HTMLElement);

  removeDataAttributes(dom as HTMLElement);

  return dom;
}

export interface CmCodeOptions {
  htmlToCmCode?: Static<(html: string) => string>;
  cmCodeToHtml?: Static<
    (markdown: string, sanitizer?: (html: string) => string) => string
  >;
  htmlSanitizer?: Static<(html: string) => string>;
}

export class CmCodeExtension extends PlainExtension {
  get name() {
    return "cmCode" as const;
  }

  onCreate(): void {
    this.store.setStringHandler(
      "cmCode",
      this.cmCodeToProsemirrorNode.bind(this)
    );
  }

  private cmCodeToProsemirrorNode(
    options: FragmentStringHandlerOptions
  ): Fragment;
  private cmCodeToProsemirrorNode(
    options: NodeStringHandlerOptions
  ): ProsemirrorNode;
  private cmCodeToProsemirrorNode(
    options: StringHandlerOptions
  ): ProsemirrorNode | Fragment {
    return cmCodeToProsemirrorNode(options as NodeStringHandlerOptions);
  }
}

declare global {
  namespace Remirror {
    interface StringHandlers {
      cmCode: CmCodeExtension;
    }

    interface AllExtensions {
      cmCode: CmCodeExtension;
    }
  }
}
